import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from '../../../environments/environment';
import { Observable, catchError, map, of, take, tap } from "rxjs";
import { IAnalyticsAlertTypes, IAnalyticsAzureCostByService, IAnalyticsHeatmapSeries, IAnalyticsOnlineSensors, IAnalyticsWeeklyAlerts } from "../../shared/models/be/analytics";
import { LoggerService } from "./utils/logger.service";
import {
    AnalyticsContext,
    AnalyticsEventComparisonLevel,
    AnalyticsEventType,
    AnalyticsHourlyHeatmapStatus,
    AnalyticsPeriod,
    IAnalyticsAzureVMMetrics,
    IAnalyticsEventComparisonOptionsResponse,
    IAnalyticsEventDetailsResponse,
    IAnalyticsEventsSummary,
    IAnalyticsEventsTrendResponse,
    IAnalyticsHourlyHeatmapResponse,
    IAnalyticsManagedEventsTrendResponse,
    IAnalyticsOperationsSummary,
    IAnalyticsResponseTimeTrendResponse,
    IUsageAnalyticsDailyResponse,
    IUsageAnalyticsHeatmapResponse,
    IUsageAnalyticsSummary,
    IUsageAnalyticsUsersQuery,
    IUsageAnalyticsUsersResponse,
    UsageAnalyticsKind
} from "../../shared/models/analytics";

@Injectable({
    providedIn: 'any'
})
export class AnalyticsService {

    constructor(
        private _http: HttpClient,
        private loggerService: LoggerService
    ) { }

    // --- HELPER PRIVATO PER COSTRUIRE I PARAMETRI ---
    // Unisce il filtro temporale (se c'è) e i campi del contesto (se non sono nulli)
    private buildParams(filter?: string, context?: AnalyticsContext): HttpParams {
        let params = new HttpParams();

        if (filter) {
            params = params.set('filter', filter);
        }

        if (context) {
            if (context.customerId) params = params.set('customerId', context.customerId);
            if (context.organizationId) params = params.set('organizationId', context.organizationId);
            if (context.buildingId) params = params.set('buildingId', context.buildingId);
            if (context.unitId) params = params.set('unitId', context.unitId);
            // Aggiungi qui room/bed se servono in futuro
        }

        return params;
    }

    public getServiceStatus$(): Observable<boolean> {
        return this._http.get<{ message: string }>(`${environment.apiEndpoint}/analytics/ping`).pipe(
            take(1),
            map((val: { message: string }) => !!val.message),
            catchError(err => {
                this.loggerService.error('Error on analytics/ping')
                return of(false);
            })
        );
    }

    public getKpiSensors$(context?: AnalyticsContext): Observable<IAnalyticsOnlineSensors> {
        // Qui non c'è filtro temporale, passo solo il contesto
        const params = this.buildParams(undefined, context);

        return this._http.get<IAnalyticsOnlineSensors>(
            `${environment.apiEndpoint}/analytics/online-sensors`,
            { params } // <--- Angular gestisce la query string
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    };

    public getKpiCost$(filter?: string): Observable<IAnalyticsAzureCostByService[]> {
        // Costi Azure sono globali, ignoro il context
        const params = this.buildParams(filter);

        return this._http.get<IAnalyticsAzureCostByService[]>(
            `${environment.apiEndpoint}/analytics/azure-costs-by-service`,
            { params }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    };

    public getKpiAlerts$(filter?: string, context?: AnalyticsContext) {
        const params = this.buildParams(filter, context);

        return this._http.get<number>(
            `${environment.apiEndpoint}/analytics/alerts`,
            { params }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    };

    public getKpiResponseTime$(filter?: string, context?: AnalyticsContext) {
        const params = this.buildParams(filter, context);

        return this._http.get(
            `${environment.apiEndpoint}/analytics/response-time`,
            {
                params,
                responseType: 'text'
            }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    };

    public getEventDetails$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext,
        eventType?: 'fall',
        page = 1,
        pageSize = 25
    ): Observable<IAnalyticsEventDetailsResponse> {
        let params = this.buildParams(filter, context)
            .set('page', page)
            .set('pageSize', pageSize);

        if (eventType) {
            params = params.set('eventType', eventType);
        }

        return this._http.get<IAnalyticsEventDetailsResponse>(
            `${environment.apiEndpoint}/analytics/events/details`,
            { params }
        ).pipe(take(1));
    }

    public getResponseTimeDetails$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext,
        page = 1,
        pageSize = 25
    ): Observable<IAnalyticsEventDetailsResponse> {
        const params = this.buildParams(filter, context)
            .set('page', page)
            .set('pageSize', pageSize);

        return this._http.get<IAnalyticsEventDetailsResponse>(
            `${environment.apiEndpoint}/analytics/response-time/details`,
            { params }
        ).pipe(take(1));
    }

    public getEventsSummary$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext
    ): Observable<IAnalyticsEventsSummary> {
        const params = this.buildParams(filter, context);

        return this._http.get<IAnalyticsEventsSummary>(
            `${environment.apiEndpoint}/analytics/events-summary`,
            { params }
        ).pipe(take(1));
    }

    public getEventsTrend$(
        eventType: AnalyticsEventType,
        filter: AnalyticsPeriod,
        context?: AnalyticsContext,
        comparisonLevel?: AnalyticsEventComparisonLevel,
        seriesIds: string[] = []
    ): Observable<IAnalyticsEventsTrendResponse> {
        let params = this.buildParams(filter, context).set('eventType', eventType);

        if (comparisonLevel) {
            params = params.set('comparisonLevel', comparisonLevel);
        }
        if (seriesIds.length) {
            params = params.set('seriesIds', seriesIds.join(','));
        }

        return this._http.get<IAnalyticsEventsTrendResponse>(
            `${environment.apiEndpoint}/analytics/events/trend`,
            { params }
        ).pipe(take(1));
    }

    public getResponseTimeTrend$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext,
        comparisonLevel?: AnalyticsEventComparisonLevel,
        seriesIds: string[] = []
    ): Observable<IAnalyticsResponseTimeTrendResponse> {
        let params = this.buildParams(filter, context);

        if (comparisonLevel) {
            params = params.set('comparisonLevel', comparisonLevel);
        }
        if (seriesIds.length) {
            params = params.set('seriesIds', seriesIds.join(','));
        }

        return this._http.get<IAnalyticsResponseTimeTrendResponse>(
            `${environment.apiEndpoint}/analytics/response-time/trend`,
            { params }
        ).pipe(take(1));
    }

    public getEventComparisonOptions$(
        level: AnalyticsEventComparisonLevel,
        context?: AnalyticsContext,
        search = '',
        page = 1,
        pageSize = 50
    ): Observable<IAnalyticsEventComparisonOptionsResponse> {
        let params = this.buildParams(undefined, context)
            .set('level', level)
            .set('page', page)
            .set('pageSize', pageSize);

        if (search.trim()) {
            params = params.set('search', search.trim());
        }

        return this._http.get<IAnalyticsEventComparisonOptionsResponse>(
            `${environment.apiEndpoint}/analytics/events/comparison-options`,
            { params }
        ).pipe(take(1));
    }

    public getOperationsSummary$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext
    ): Observable<IAnalyticsOperationsSummary> {
        const params = this.buildParams(filter, context);

        return this._http.get<IAnalyticsOperationsSummary>(
            `${environment.apiEndpoint}/analytics/operations-summary`,
            { params }
        ).pipe(take(1));
    }

    public getManagedEventsTrend$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext
    ): Observable<IAnalyticsManagedEventsTrendResponse> {
        const params = this.buildParams(filter, context);

        return this._http.get<IAnalyticsManagedEventsTrendResponse>(
            `${environment.apiEndpoint}/analytics/managed-events/trend`,
            { params }
        ).pipe(take(1));
    }

    public getHourlyHeatmapDetails$(
        filter: AnalyticsPeriod,
        status: AnalyticsHourlyHeatmapStatus,
        context?: AnalyticsContext
    ): Observable<IAnalyticsHourlyHeatmapResponse> {
        const params = this.buildParams(filter, context).set('status', status);

        return this._http.get<IAnalyticsHourlyHeatmapResponse>(
            `${environment.apiEndpoint}/analytics/hourly-heatmap`,
            { params }
        ).pipe(take(1));
    }

    public getUsageSummary$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext
    ): Observable<IUsageAnalyticsSummary> {
        const params = this.buildParams(filter, context);
        return this._http.get<IUsageAnalyticsSummary>(
            `${environment.apiEndpoint}/analytics/usage/summary`,
            { params }
        ).pipe(take(1));
    }

    public getUsageUsers$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext,
        query: IUsageAnalyticsUsersQuery = {}
    ): Observable<IUsageAnalyticsUsersResponse> {
        let params = this.buildParams(filter, context);
        if (query.search?.trim()) params = params.set('search', query.search.trim());
        if (query.page) params = params.set('page', query.page);
        if (query.pageSize) params = params.set('pageSize', query.pageSize);
        if (query.sortBy) params = params.set('sortBy', query.sortBy);
        if (query.sortDirection) params = params.set('sortDirection', query.sortDirection);

        return this._http.get<IUsageAnalyticsUsersResponse>(
            `${environment.apiEndpoint}/analytics/usage/users`,
            { params }
        ).pipe(take(1));
    }

    public getUsageDaily$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext
    ): Observable<IUsageAnalyticsDailyResponse> {
        const params = this.buildParams(filter, context);
        return this._http.get<IUsageAnalyticsDailyResponse>(
            `${environment.apiEndpoint}/analytics/usage/daily`,
            { params }
        ).pipe(take(1));
    }

    public getUsageHeatmap$(
        filter: AnalyticsPeriod,
        context?: AnalyticsContext,
        kind: UsageAnalyticsKind = 'application_usage'
    ): Observable<IUsageAnalyticsHeatmapResponse> {
        const params = this.buildParams(filter, context).set('kind', kind);
        return this._http.get<IUsageAnalyticsHeatmapResponse>(
            `${environment.apiEndpoint}/analytics/usage/heatmap`,
            { params }
        ).pipe(take(1));
    }

    public getHierarchyStats$(filter?: string, context?: AnalyticsContext): Observable<any[]> {
        const params = this.buildParams(filter, context);
        return this._http.get<any[]>(
            `${environment.apiEndpoint}/analytics/hierarchy-stats`,
            { params }
        ).pipe(
            take(1),
            catchError(err => { throw 'err'; })
        );
    };

    public getWeeklyAlerts$(filter?: string, context?: AnalyticsContext) {
        const params = this.buildParams(filter, context);

        return this._http.get<IAnalyticsWeeklyAlerts[]>(
            `${environment.apiEndpoint}/analytics/weekly-alerts`,
            { params }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    };

    public getAlertTypes$(filter?: string, context?: AnalyticsContext) {
        const params = this.buildParams(filter, context);

        return this._http.get<IAnalyticsAlertTypes[]>(
            `${environment.apiEndpoint}/analytics/alert-types`,
            { params }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        )
    };

    public getHourlyAlerts$(filter?: string, context?: AnalyticsContext) {
        const params = this.buildParams(filter, context);

        return this._http.get<IAnalyticsHeatmapSeries[]>(
            `${environment.apiEndpoint}/analytics/hourly-heatmap`,
            { params }
        ).pipe(
            take(1),
            catchError(err => {
                throw 'error in source. Details: ' + err;
            })
        );
    };

    // ========================================================================
    // Azure VM Metrics (GLOBALE - Ignora Context)
    // ========================================================================
    public getAzureVMMetrics$(): Observable<IAnalyticsAzureVMMetrics[]> {

        return this._http.get<IAnalyticsAzureVMMetrics[]>(
            `${environment.apiEndpoint}/analytics/azure-vm-metrics`
        ).pipe(
            take(1),
            catchError(err => {
                this.loggerService.error('Error fetching Azure VM Metrics: ' + err);
                throw 'error in source. Details: ' + err;
            })
        );
    }
}
